import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Scissors, ArrowLeft, Plus, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import ExportExcelButton from '@/components/ExportExcelButton';
import { useAuth } from '@/contexts/AuthContext';
import { fetchCortes, deleteCorte } from '@/services/cortesInventario.service';
import CrearCorteModal from '@/components/cortesInventario/CrearCorteModal';

const ITEMS_PER_PAGE = 20;

const CortesInventarioPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [cortes, setCortes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isCorteModalOpen, setIsCorteModalOpen] = useState(false);

  const loadCortes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchCortes({ page: currentPage, limit: ITEMS_PER_PAGE });
      setCortes(response.cortes || []);
      setTotalItems(response.total || 0);
    } catch (error) {
      console.error('Error al cargar cortes:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los cortes',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, toast]);

  useEffect(() => {
    loadCortes();
  }, [loadCortes]);

  const handleCorteSuccess = () => {
    setIsCorteModalOpen(false);
    setCurrentPage(1);
    loadCortes();
  };

  const handleAction = async (actionKey, row) => {
    if (actionKey === 'view') {
      navigate(`/lavanderia/cortes-inventario/${row._id}`);
    } else if (actionKey === 'delete') {
      if (!window.confirm(
        `¿Eliminar el corte ${row.consecutivo}?\n\nSe borrarán el corte y sus movimientos históricos. El inventario actual NO se modifica.`
      )) return;
      try {
        await deleteCorte(row._id);
        toast({ title: 'Corte eliminado', description: `${row.consecutivo} eliminado correctamente` });
        loadCortes();
      } catch (error) {
        toast({ title: 'Error', description: error.message || 'No se pudo eliminar', variant: 'destructive' });
      }
    }
  };

  const formatFecha = (fecha) => fecha ? new Date(fecha).toLocaleDateString('es-ES') : '-';

  const columns = [
    {
      id: 'consecutivo',
      label: 'Corte',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Scissors className="h-4 w-4 text-indigo-600" />
          <span className="font-medium">{row.consecutivo}</span>
        </div>
      )
    },
    {
      id: 'fecha_corte',
      label: 'Fecha',
      render: (row) => formatFecha(row.fecha_corte)
    },
    {
      id: 'periodo',
      label: 'Período',
      render: (row) => `${row.periodo_desde ? formatFecha(row.periodo_desde) : 'Inicio'} → ${formatFecha(row.periodo_hasta)}`
    },
    {
      id: 'total_prendas',
      label: 'Prendas',
      render: (row) => <Badge variant="outline">{row.totales?.total_prendas ?? 0}</Badge>
    },
    {
      id: 'total_inicial',
      label: 'Inicial',
      render: (row) => <span className="text-gray-600">{row.totales?.total_inicial ?? 0}</span>
    },
    {
      id: 'total_real',
      label: 'Real (final)',
      render: (row) => <span className="font-medium">{row.totales?.total_real ?? 0}</span>
    },
    {
      id: 'total_diferencia',
      label: 'Diferencia',
      render: (row) => {
        const diff = row.totales?.total_diferencia ?? 0;
        return (
          <span className={`font-semibold ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {diff > 0 ? '+' : ''}{diff}
          </span>
        );
      }
    },
    {
      id: 'notas',
      label: 'Notas',
      render: (row) => <span className="text-sm text-gray-500 truncate max-w-[200px] block">{row.notas || '-'}</span>
    },
    {
      id: 'usuario',
      label: 'Usuario',
      render: (row) => row.usuario?.nombre || 'N/A'
    },
    {
      id: 'actions',
      key: 'actions',
      label: 'Acciones',
      actions: [
        { key: 'view', icon: 'Eye', tooltip: 'Ver detalle', className: 'text-indigo-600 hover:bg-indigo-50' },
        ...(user?.role === 'admin'
          ? [{ key: 'delete', icon: 'Trash2', tooltip: 'Eliminar corte', className: 'text-red-600 hover:bg-red-50' }]
          : [])
      ]
    }
  ];

  const exportColumns = [
    { header: 'Corte', key: 'consecutivo' },
    { header: 'Fecha', key: 'fecha_corte', formatDate: true },
    { header: 'Período Desde', key: 'periodo_desde', formatDate: true },
    { header: 'Período Hasta', key: 'periodo_hasta', formatDate: true },
    { header: 'Total Prendas', key: 'totales.total_prendas' },
    { header: 'Total Inicial', key: 'totales.total_inicial' },
    { header: 'Total Final (Sistema)', key: 'totales.total_final' },
    { header: 'Total Real', key: 'totales.total_real' },
    { header: 'Diferencia', key: 'totales.total_diferencia' },
    { header: 'Notas', key: 'notas' },
    { header: 'Usuario', key: 'usuario.nombre' }
  ];

  const exportService = async ({ query, limit, page }) => {
    const response = await fetchCortes({ ...query, limit, page });
    return { data: response.cortes || [] };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/lavanderia/inventario')} className="h-10 w-10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Scissors className="h-8 w-8 text-indigo-600" />
              Cortes de Inventario
            </h1>
            <p className="text-gray-600 mt-1">
              Cierres de período: conteo físico, inventario inicial/final y análisis por corte
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ExportExcelButton
            service={exportService}
            filters={{}}
            columns={exportColumns}
            fileName={`cortes_inventario_${new Date().toISOString().split('T')[0]}`}
          />
          {user?.role === 'admin' && (
            <Button onClick={() => setIsCorteModalOpen(true)} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4" />
              Generar Corte
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Historial de Cortes</CardTitle>
          <CardDescription>
            Cada corte guarda el inventario y los movimientos del período. Haz clic en “Ver detalle” para el kardex y el PDF.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataTable
            columns={columns}
            data={cortes}
            isLoading={isLoading}
            showDefaultActions={false}
            onAction={handleAction}
          />
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </CardContent>
      </Card>

      <CrearCorteModal
        isOpen={isCorteModalOpen}
        onClose={() => setIsCorteModalOpen(false)}
        onSuccess={handleCorteSuccess}
        user={user}
      />
    </motion.div>
  );
};

export default CortesInventarioPage;
